import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { settingsService, ImageSettings } from '../../services/settingsService';

const ImageSettingsContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-gray-800);
  }
  
  p {
    margin: 0.5rem 0 0 0;
    color: var(--color-gray-600);
  }
`;

const SettingsGrid = styled.div`
  display: grid;
  gap: 2rem;
`;

const SettingCard = styled.div`
  background: var(--color-white);
  padding: 2rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  
  h3 {
    margin: 0 0 1rem 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-gray-800);
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  p {
    margin: 0 0 1.5rem 0;
    color: var(--color-gray-600);
    line-height: 1.5;
  }
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingLabel = styled.div`
  flex: 1;
  
  h4 {
    margin: 0 0 0.25rem 0;
    font-size: 1rem;
    font-weight: 500;
    color: var(--color-gray-800);
  }
  
  span {
    font-size: 0.875rem;
    color: var(--color-gray-600);
  }
`;

const SettingControl = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 24px;
    
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: .4s;
      border-radius: 50%;
    }
  }
  
  input:checked + span {
    background-color: var(--color-primary);
  }
  
  input:checked + span:before {
    transform: translateX(26px);
  }
`;

const Input = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  background: var(--color-white);
  font-size: 0.875rem;
  width: 150px;
`;

const SaveButton = styled.button`
  padding: 0.75rem 2rem;
  background: var(--color-primary);
  color: var(--color-white);
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: var(--color-primary-dark);
  }
  
  &:disabled {
    background: var(--color-gray-400);
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span<{ $status: 'success' | 'warning' | 'error' }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => 
    props.$status === 'success' ? '#D1FAE5' :
    props.$status === 'warning' ? '#FEF3C7' :
    '#FEE2E2'
  };
  color: ${props => 
    props.$status === 'success' ? '#065F46' :
    props.$status === 'warning' ? '#92400E' :
    '#991B1B'
  };
`;

const ImageSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<ImageSettings>({
    maxImageSizeKb: 450, // Default 450KB as requested
    defaultMaxImageSizeKb: 450, // Default 450KB as requested
    maxImagesPerStory: 5, // Default 5 images for free users
    enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getImageSettings();
      setSettings(data);
    } catch (error) {
      console.error('Failed to load image settings:', error);
      toast.error('Failed to load image settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const updatedSettings = await settingsService.updateImageSettings(settings);
      setSettings(updatedSettings);
      toast.success('Image settings saved successfully!');
    } catch (error) {
      console.error('Failed to save image settings:', error);
      toast.error('Failed to save image settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key: keyof ImageSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (loading) {
    return (
      <ImageSettingsContainer>
        <PageHeader>
          <h1>🖼️ Image Upload Settings</h1>
          <p>Loading settings...</p>
        </PageHeader>
      </ImageSettingsContainer>
    );
  }

  return (
    <ImageSettingsContainer>
      <PageHeader>
        <h1>🖼️ Image Upload Settings</h1>
        <p>Configure image upload limits and restrictions for story creation</p>
      </PageHeader>

      <SettingsGrid>
        {/* Image Upload Configuration */}
        <SettingCard>
          <h3>📁 Upload Configuration</h3>
          <p>Configure maximum file sizes and limits for image uploads in stories</p>
          
          <SettingRow>
            <SettingLabel>
              <h4>Enable Image Uploads</h4>
              <span>Allow users to upload images in stories</span>
            </SettingLabel>
            <SettingControl>
              <Toggle>
                <input 
                  type="checkbox" 
                  checked={settings.enabled}
                  onChange={(e) => handleSettingChange('enabled', e.target.checked)}
                />
                <span></span>
              </Toggle>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Maximum Image Size (System Limit)</h4>
              <span>Absolute maximum file size per image in KB (1MB = 1024KB)</span>
            </SettingLabel>
            <SettingControl>
              <Input
                type="number"
                min="1"
                max="10240"
                value={settings.maxImageSizeKb}
                onChange={(e) => handleSettingChange('maxImageSizeKb', parseInt(e.target.value))}
              />
              <span>KB ({Math.round(settings.maxImageSizeKb / 1024 * 100) / 100}MB)</span>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Default Max Picture Size</h4>
              <span>Default maximum file size for new users and stories</span>
            </SettingLabel>
            <SettingControl>
              <Input
                type="number"
                min="1"
                max={settings.maxImageSizeKb}
                value={settings.defaultMaxImageSizeKb}
                onChange={(e) => handleSettingChange('defaultMaxImageSizeKb', parseInt(e.target.value))}
              />
              <span>KB ({Math.round(settings.defaultMaxImageSizeKb / 1024 * 100) / 100}MB)</span>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Maximum Images Per Story</h4>
              <span>Maximum number of images allowed per story</span>
            </SettingLabel>
            <SettingControl>
              <Input
                type="number"
                min="1"
                max="100"
                value={settings.maxImagesPerStory}
                onChange={(e) => handleSettingChange('maxImagesPerStory', parseInt(e.target.value))}
              />
              <span>images</span>
            </SettingControl>
          </SettingRow>
        </SettingCard>

        {/* Current Settings Status */}
        <SettingCard>
          <h3>📊 Current Settings Status</h3>
          <p>Overview of current configuration and recommended settings</p>
          
          <SettingRow>
            <SettingLabel>
              <h4>Image Uploads Status</h4>
              <span>Whether image uploads are currently enabled</span>
            </SettingLabel>
            <SettingControl>
              <StatusBadge $status={settings.enabled ? 'success' : 'error'}>
                {settings.enabled ? 'Enabled' : 'Disabled'}
              </StatusBadge>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>System Maximum</h4>
              <span>Absolute maximum image size limit</span>
            </SettingLabel>
            <SettingControl>
              <StatusBadge $status="success">
                {settings.maxImageSizeKb}KB 
                ({Math.round(settings.maxImageSizeKb / 1024 * 100) / 100}MB)
              </StatusBadge>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Default Limit</h4>
              <span>Default maximum image size for users</span>
            </SettingLabel>
            <SettingControl>
              <StatusBadge $status="success">
                {settings.defaultMaxImageSizeKb}KB 
                ({Math.round(settings.defaultMaxImageSizeKb / 1024 * 100) / 100}MB)
              </StatusBadge>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Images Per Story Limit</h4>
              <span>Current maximum images per story setting</span>
            </SettingLabel>
            <SettingControl>
              <StatusBadge $status="success">
                {settings.maxImagesPerStory} images
              </StatusBadge>
            </SettingControl>
          </SettingRow>
        </SettingCard>

        {/* Recommendations */}
        <SettingCard>
          <h3>💡 Recommendations</h3>
          <p>Suggested settings for optimal performance and user experience</p>
          
          <SettingRow>
            <SettingLabel>
              <h4>Free Users (Default Plan)</h4>
              <span>Recommended limits for free tier users</span>
            </SettingLabel>
            <SettingControl>
              <span style={{ color: 'var(--color-gray-600)' }}>450KB max, 5 images per story</span>
            </SettingControl>
          </SettingRow>

          <SettingRow>
            <SettingLabel>
              <h4>Configuration Active</h4>
              <span>Settings are applied and active</span>
            </SettingLabel>
            <SettingControl>
              <StatusBadge $status="success">
                Active
              </StatusBadge>
            </SettingControl>
          </SettingRow>
        </SettingCard>

        {/* Save Settings */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
          <SaveButton onClick={handleSaveSettings} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </SaveButton>
        </div>
      </SettingsGrid>
    </ImageSettingsContainer>
  );
};

export default ImageSettingsPage;